<script lang="ts">
  /**
   * The thumb and its count — and, where a host can perform one, the like itself.
   *
   * ONE DEFINITION, THREE CALL SITES. This markup was copy-pasted in `SkoolPost` and
   * twice in `SkoolComment` (the quoted variant and the bubble), which is exactly the
   * shape in which "make it clickable" becomes three near-identical implementations of
   * an optimistic write. It is a component so the behaviour has one home.
   *
   * LIKING IS A ONE-WAY DOOR, on purpose and by product decision. There is no unlike
   * here and no toggle: once it is liked the control goes inert. A like is being used as
   * a "this is dealt with" signal, so its inverse would mean un-answering something,
   * and the code should not be able to express that at all.
   *
   * ALREADY LIKED IS SUCCESS, not an error. Skool accepts a repeat like as a 200 and
   * leaves the count where it is (verified: `{old:'', new:'up'}` twice on the same post
   * → 200, 200, count 1). So a host that discovers the owner had already liked it in
   * Skool's own UI reports success, and the only visible difference is that the count
   * does not move.
   *
   * The paint is optimistic and reverts on a throw, which is the same rule the reply
   * composer and the notification marks follow: the person pressed it, so it should look
   * pressed, and a failure must never leave a lie on screen.
   */
  let {
    count = 0,
    liked = false,
    onLike = null,
    compact = false,
  }: {
    count?: number | null;
    /** Already liked, by us or previously in Skool. Renders filled and inert. */
    liked?: boolean;
    /** Absent means a read-only readout — a surface with no Skool session to write
     *  with, or a row nothing can act on. */
    onLike?: (() => Promise<void> | void) | null;
    compact?: boolean;
  } = $props();

  let busy = $state(false);
  /** Set on a successful like so the control settles into its liked state without the
   *  host having to feed `liked` back down. */
  let didLike = $state(false);
  let bumped = $state(0);

  const isLiked = $derived(liked || didLike);
  const shown = $derived((count ?? 0) + bumped);
  const canLike = $derived(!!onLike && !isLiked && !busy);

  async function like(): Promise<void> {
    if (!onLike || isLiked || busy) return;
    busy = true;
    // Paint first: the count moves and the thumb fills on the press.
    didLike = true;
    bumped = 1;
    try {
      await onLike();
    } catch {
      // Put it back. The host reports the failure — this only stops the UI claiming a
      // like that did not happen.
      didLike = false;
      bumped = 0;
    } finally {
      busy = false;
    }
  }
</script>

{#if canLike || busy}
  <button
    type="button"
    class="inline-flex items-center gap-1 rounded transition-colors hover:text-primary
           {isLiked ? 'text-primary' : ''}"
    onclick={like}
    disabled={busy}
    title="Like — and mark this answered"
    aria-label="Like"
  >
    <svg
      class="{compact ? 'h-3.5 w-3.5' : 'h-3.5 w-3.5'}"
      viewBox="0 0 24 24"
      fill={isLiked ? 'currentColor' : 'none'}
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    ><path d="M7 10v11" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" /></svg>
    {shown}
  </button>
{:else}
  <!-- Static: either nothing can perform a like here, or it is already liked and the
       control is deliberately spent. -->
  <span class="inline-flex items-center gap-1 {isLiked ? 'text-primary' : ''}" title={isLiked ? 'Liked' : undefined}>
    <svg
      class="h-3.5 w-3.5"
      viewBox="0 0 24 24"
      fill={isLiked ? 'currentColor' : 'none'}
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    ><path d="M7 10v11" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" /></svg>
    {shown}
  </span>
{/if}
